import React, { useContext, useEffect, useState } from 'react';
import { getAccountBalances, getAccountTransactions, transactionsLimit } from "../API/Ledger";
import {
    Container,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Paper,
    Typography,
    Box,
    Card,
    CardContent,
    Chip, 
    InputAdornment,
    FormControl,
    InputLabel
} from "@mui/material";
import { CONSULTING_SERVICES, getServiceById } from '../constants/services';
import {
    findRefundedTransactions,
    formatDateTime,
    formatMoney,
    formatMoneyTextColor, isBusinessWithoutSubscription,
    isNonCloudWithoutSubscriptionOrDeveloper,
    hasSubscription
} from "../utils";
import {colorEasyRed, colorGreen, colorSecondary, colorText} from "../App/theme";
import {
    getFullTokenNameByResourceId, getQueryParamValue,
    getTokenNameByResourceId,
    isMobile,
    parkedDomain,
    RESOURCE
} from "../App/utils";
import { IconButton, Tooltip, Button, TextField } from "@mui/material";
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

import { createStripeCheckout, refund } from "../API/Stripe";
import LocalLoader from "../Loaders/LocalLoader";
import { TransactionActionsMenu } from "./TransactionActionsMenu";
import AnimatedNumber from "./AnimatedNumber";
import LinearProgress from "@mui/material/LinearProgress";

import { ChatModels, VisualModels, AudioModels, MyServices, WebTools } from "../constants";
import { GAEvent } from "../App/GA";
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import { getKBsSharedWithMe, listKBs, getDomainPublicSettings } from "../API/KB/KBAPI";
import { decrypt, decryptKBItem } from "../App/cryptoUtils";
import { signKBTransactionJWT } from "../Crypto/utils";
import axios from "axios";
import { AlertContext } from "../App/App";
import {useConfirm} from "../Components/ConfirmContext";
import { Dialog, DialogTitle, DialogContent } from "@mui/material";
import {createUserTypeSelectionDialog} from "./BillingHelpers";

let serviceAccounts = {}
Object.entries(MyServices).forEach(([key, data]) => {
    if (data?.accountId) serviceAccounts[data?.accountId] = data?.name
})

export const UserWallets = ({ userProfile }) => {
    const [loadingTransactions, setLoadingTransactions] = useState(false);
    const [loadingBalances, setLoadingBalances] = useState(false);
    const [loadingBlocking, setLoadingBlocking] = useState(false);
    const [transactions, setTransactions] = useState([]);
    const [rowBalance, setBalances] = useState([]);
    const [depositAmountEUR, setDepositAmountEUR] = useState('10');
    const [isAmountValid, setIsAmountValid] = useState(true);
    const [selectedService, setSelectedService] = useState(getQueryParamValue('service') || 'credits');
    const [showServiceMenu, setShowServiceMenu] = useState(false);
    const [lastEvaluatedKey, setLastEvaluatedKey] = useState(null);
    const [hasMoreTransactions, setHasMoreTransactions] = useState(true);
    const [resources, setResources] = useState([]);
    const [applicationAccounts, setApplicationAccounts] = useState([]);
    const [selectedAccount, setSelectedAccount] = useState('');
    const [selectedTransactionsAccount, setSelectedTransactionsAccount] = useState('');
    const [transferAmount, setTransferAmount] = useState('1000');
    const [customAccountId, setCustomAccountId] = useState('');
    const [showUserTypeDialog, setShowUserTypeDialog] = useState(false);
    const [isDeveloper, setIsDeveloper] = useState(null);
    const [creditPricing, setCreditPricing] = useState(null);
    const [loadingPricing, setLoadingPricing] = useState(!!parkedDomain); // Loading if parked domain
    const [hideTransactions, setHideTransactions] = useState(false);
    const [settingsLoaded, setSettingsLoaded] = useState(!parkedDomain); // Only need to load for parked domains
    const { setSystemAlert } = useContext(AlertContext);

    useEffect(() => {
        const loadAccounts = async () => {
            const accounts = (await listKBs())?.filter(o => o.kbAccount);
            setApplicationAccounts(accounts);
            setSelectedAccount(accounts[0]?.accountId);
        };
        loadAccounts();

        // Load custom credit pricing if using parked domain
        const loadCreditPricing = async () => {
            if (parkedDomain) {
                setLoadingPricing(true);
                try {
                    const settings = await getDomainPublicSettings(parkedDomain);
                    if (settings?.CREDIT_PRICING) {
                        // Check if credit pricing is explicitly disabled
                        if (settings.CREDIT_PRICING.disabled === true) {
                            setCreditPricing({ disabled: true });
                        } else {
                            setCreditPricing(settings.CREDIT_PRICING);
                        }
                    }
                    // Check for Advanced Settings - Hide Transactions
                    if (settings?.ADVANCED_SETTINGS?.TRANSACTIONS === false && !getQueryParamValue('admin')) {
                        setHideTransactions(true);
                    }
                    // If no CREDIT_PRICING, leave creditPricing as null - form won't show
                } catch (error) {
                    console.error('Error loading credit pricing:', error);
                    // On error, leave creditPricing as null - form won't show
                } finally {
                    setLoadingPricing(false);
                    setSettingsLoaded(true); // Mark as loaded after settings fetch completes
                }
            } else {
                // No parked domain - use OpenKBS defaults (null means use defaults)
                setLoadingPricing(false);
            }
        };
        loadCreditPricing();

        // Handle URL parameters for preset service and amount
        const urlParams = new URLSearchParams(window.location.search);
        const serviceParam = urlParams.get('service');
        const amountParam = urlParams.get('amount');
        
        // Show service menu only if service parameter is present
        if (serviceParam) {
            setShowServiceMenu(true);
            // Check if service exists in the services configuration
            const serviceExists = Object.values(CONSULTING_SERVICES).some(service => service.id === serviceParam);
            if (serviceExists) {
                setSelectedService(serviceParam);
            }
        } else {
            setShowServiceMenu(false);
        }
        
        if (amountParam && !isNaN(parseFloat(amountParam))) {
            const amount = parseFloat(amountParam);
            // Determine max amount based on service type
            let maxAmount = 50000; // Default for consulting services
            const currentService = serviceParam || 'credits';
            if (currentService === 'credits') {
                maxAmount = userProfile?.params?.isCloud ? 50000 : 50000;
            }
            
            if (amount >= 10 && amount <= maxAmount) {
                setDepositAmountEUR(amount.toString());
                setIsAmountValid(true);
            }
        }
    }, [userProfile]);

    useEffect(() => {
        if (!userProfile?.accountId) return;
        if (!selectedTransactionsAccount) {
            setSelectedTransactionsAccount(userProfile?.accountId);
        }
    }, [userProfile, selectedTransactionsAccount]);

    const handleTransferAmountChange = (event) => {
        setTransferAmount(event.target.value);
    };

    const handleAccountChange = (event) => {
        setSelectedAccount(event.target.value);
    };

    const handleTransactionsAccountChange = (event) => {
        // Set new account
        setSelectedTransactionsAccount(event.target.value);
        // Reset pagination state when changing accounts
        setLastEvaluatedKey(null);
        setHasMoreTransactions(true);
    };

    const handleTransaction = async (isFunding) => {
        const isTransferMode = getQueryParamValue('transfer');
        const targetAccount = isFunding ? (isTransferMode && customAccountId ? customAccountId : selectedAccount) : userProfile?.accountId;
        const sourceAccount = isFunding ? userProfile : (isTransferMode && customAccountId ? { accountId: customAccountId } : applicationAccounts?.find(o => o.accountId === selectedAccount));

        const toAccountId = isFunding ? targetAccount : userProfile?.accountId;
        const accountId = sourceAccount?.accountId;
        const maxAmount = parseInt(transferAmount * 1000);
        const walletPrivateKey = isFunding ? userProfile?.walletPrivateKey : (isTransferMode && customAccountId ? null : decrypt(sourceAccount?.walletPrivateKey, sourceAccount?.key));
        const walletPublicKey = isFunding ? userProfile?.walletPublicKey : sourceAccount?.walletPublicKey;

        const transactionJWT = await signKBTransactionJWT({
            maxAmount, toAccountId, accountId, walletPrivateKey, walletPublicKey
        });

        try {
            const response = await axios.get(`https://ledger.openkbs.com/transfer?transactionJWT=${encodeURIComponent(transactionJWT)}` +
                `&requestedAmount=${maxAmount}` +
                `&requestedMessage=internal transfer`);

            if (response?.data?.message && response?.status === 200) {
                setSystemAlert({ msg: response.data.message, type: 'success' });
            }
        } catch (e) {
            setSystemAlert({ msg: 'Transaction failed: Insufficient funds or network error', type: 'error' });
        }
    };

    const handleFund = () => handleTransaction(true);
    const handleGetBack = () => handleTransaction(false);

    const handleDepositChange = (event) => {
        let value = event.target.value;
        
        // Set different limits based on service type
        let maxAmount = 50000; // Default for consulting services
        if (selectedService === 'credits') {
            // Keep existing limits for credits
            maxAmount = !userProfile?.params?.isCloud ? 50000 : 50000;
        }
        
        if (parseInt(value) > maxAmount) {
            value = maxAmount.toString();
        }

        if (parseInt(value) < 10) {
            setIsAmountValid(false);
        } else {
            setIsAmountValid(true);
        }
        setDepositAmountEUR(value);
    };

    const showConfirm = useConfirm();
    
    const proceedToStripe = async () => {
        // if (userProfile?.subscriptionPlan && userProfile?.subscriptionPlan !== 'free') {
        //     const daysRemaining = userProfile.currentPeriodEnd ?
        //         Math.ceil((new Date(userProfile.currentPeriodEnd) - new Date()) / (1000 * 60 * 60 * 24)) : 0;
        //
        //     const expiryText = daysRemaining > 0
        //         ? `(in ${daysRemaining} day${daysRemaining === 1 ? '' : 's'})`
        //         : '(today)';
        //
        //     const confirmed = await showConfirm({
        //         title: 'Monthly Plan Token Purchase',
        //         content: `Please note that any tokens you purchase will expire at the end of your current subscription period ${expiryText}. Do you want to proceed?`,
        //         confirmText: 'Buy Now'
        //     });
        //
        //     if (!confirmed) {
        //         return; // Exit if user cancels
        //     }
        // }

        try {
            setLoadingBlocking(true);

            GAEvent({
                category: "ecommerce",
                action: "buy_button_click",
                value: parseInt(depositAmountEUR)
            });

            const session = await createStripeCheckout(depositAmountEUR, userProfile, selectedService);

            if (session?.url) {
                window.location.href = session.url;
            }

        } catch (error) {
            console.error('Error:', error);
        } finally {
            setLoadingBlocking(false);
        }
    };

    const handleBuyClick = async () => {
        // Check if user needs to select account type first (only for credits purchases)
        if (selectedService === 'credits' && withoutSubscriptionOrDeveloper && isDeveloper === null) {
            setShowUserTypeDialog(true);
            return;
        }

        await proceedToStripe();
    };

    const UserTypeSelectionDialog = createUserTypeSelectionDialog({showUserTypeDialog, setShowUserTypeDialog, setIsDeveloper, proceedToStripe})

    const transactionMap = (transaction) => {
        const params = {
            id: transaction.transactionId,
            date: formatDateTime(transaction.transactionId.split('-')[0]),
            reason: transaction.message,
            remoteAccount: transaction.remoteAccount,
            accountId: transaction.accountId,
            amount: transaction.amount,
            resourceId: transaction.resourceId,
        }

        if (transaction.subjectId) params.subjectId = transaction.subjectId;
        if (transaction.subject) params.subject = transaction.subject;

        return params;
    }

    const isNew = (t) => (+new Date() - parseInt(t.id.split('-')[0])) < 1000 * 30;

    const fetchBalancesAndTransactions = ({ withLoaders = true }) => {
        if (withLoaders) {
            setLoadingBalances(true);
            setLoadingTransactions(true);
        }

        // Use the current value of selectedTransactionsAccount if it exists, otherwise use default (user account)
        const accountIdToFetch = selectedTransactionsAccount || userProfile?.accountId;
        const account = accountIdToFetch === userProfile?.accountId ? userProfile : applicationAccounts.find(o => o.accountId === accountIdToFetch);
        
        // Ensure we have the account before proceeding
        if (!account?.accountId) {
            console.warn("No account found for transactions fetch");
            if (withLoaders) {
                setLoadingBalances(false);
                setLoadingTransactions(false);
            }
            return;
        }
        
        const walletPrivateKey = accountIdToFetch === userProfile?.accountId ? userProfile.walletPrivateKey : decrypt(account?.walletPrivateKey, account?.key);
        const walletPublicKey = account?.walletPublicKey;

        getAccountBalances(account.accountId, walletPrivateKey, walletPublicKey)
            .then((balances) => {
                setBalances(balances);
                if (withLoaders) setLoadingBalances(false);
            });

        getAccountTransactions(account.accountId, walletPrivateKey, walletPublicKey)
            .then((fetchedTransactions) => {
                const formattedTransactions = fetchedTransactions.transactions.map(t => ({
                    ...transactionMap(t),
                }));
                setTransactions(formattedTransactions);
                if (withLoaders) setLoadingTransactions(false);
            });
    }

    useEffect(() => {
        const handleWebsocketMessage = (event) => {
            const data = JSON.parse(event.detail);
            if (data?.tableName === "ledger-account") {
                fetchBalancesAndTransactions({ withLoaders: false });
            }
        };

        window.addEventListener('onWebsocketMessage', handleWebsocketMessage);

        return () => {
            window.removeEventListener('onWebsocketMessage', handleWebsocketMessage);
        };
    }, [userProfile, selectedAccount, selectedTransactionsAccount, applicationAccounts]);

    useEffect(() => {
        fetchBalancesAndTransactions({ withLoaders: true });
    }, [userProfile, selectedTransactionsAccount]);

    useEffect(() => {
        const AIResources = [
            {
                items: RESOURCE.tokens,
                suffix: RESOURCE.tokens,
                itemPrice: 1000,
                model: 'Credits',
                label: `Credits to buy`,
            },
        ];

        const calculateResources = () => {
            // Use custom pricing if available, otherwise default (1000 credits for €10)
            let creditsPerUnit = 100; // Default: 100 credits per €1

            if (creditPricing?.creditsPer10Units) {
                // Custom pricing: creditsPer10Units is how many credits for 10 units (€10 or $10)
                creditsPerUnit = creditPricing.creditsPer10Units / 10;
            }

            const tokens = depositAmountEUR * creditsPerUnit * 1000; // Multiply by 1000 for internal representation

            return AIResources.map(resource => ({
                ...resource,
                quantity: Math.round(tokens / resource.itemPrice),
            }));
        };

        setResources(calculateResources())
    }, [depositAmountEUR, creditPricing]);

    const maxTokens = 100000;

    const loadMoreTransactions = async () => {
        // Use selectedTransactionsAccount instead of selectedAccount for consistency
        const accountIdToFetch = selectedTransactionsAccount || userProfile?.accountId;
        if (!accountIdToFetch || !hasMoreTransactions) return;

        setLoadingBlocking(true);
        const account = accountIdToFetch === userProfile?.accountId ? userProfile : applicationAccounts.find(o => o.accountId === accountIdToFetch);
        
        if (!account?.accountId) {
            console.warn("No account found for loading more transactions");
            setLoadingBlocking(false);
            return;
        }
        
        const walletPrivateKey = accountIdToFetch === userProfile?.accountId ? userProfile.walletPrivateKey : decrypt(account?.walletPrivateKey, account?.key);
        const walletPublicKey = account?.walletPublicKey;

        try {
            const data = await getAccountTransactions(account.accountId, walletPrivateKey, walletPublicKey, lastEvaluatedKey);
            const newTransactions = data.transactions.map(transactionMap);
            setTransactions(prevTransactions => [...prevTransactions, ...newTransactions]);
            setLoadingBlocking(false);
            if (data.lastEvaluatedKey) {
                setLastEvaluatedKey(data.lastEvaluatedKey);
            } else {
                setHasMoreTransactions(false);
            }
        } catch (error) {
            console.error('Error fetching transactions:', error);
            setLoadingBlocking(false);
        }
    };

    const handleRefund = async (paymentIntentId, reason, customerReason) => {
        try {
            setLoadingBlocking(true);
            const response = await refund(paymentIntentId, reason, customerReason);
        } catch (error) {
            console.error('Refund error:', error);
        } finally {
            setLoadingBlocking(false);
        }
    };

    const mobileStyle = { marginLeft: '-5%', width: '110%' };
    const balance = rowBalance?.find(o => o?.resourceId === RESOURCE.tokens)?.balance;

    let namedAccounts = {};

    rowBalance.forEach(a => {
        if (a.accountId && a.accountName) {
            namedAccounts[a.accountId] = a.accountName;
        }
    })

    const withoutSubscriptionOrDeveloper = isNonCloudWithoutSubscriptionOrDeveloper(userProfile);
    const refunded = transactions?.length ? findRefundedTransactions(transactions) : {};
    // const showTransferTokens = true // @Todo // userProfile?.params?.isCloud;
    const showTransferTokens = userProfile?.params?.isCloud || getQueryParamValue('transfer')

    // If parked domain and still loading settings, return null to avoid showing default values
    if (parkedDomain && loadingPricing) {
        return null;
    }

    // Check if credit pricing is disabled
    const isCreditPricingDisabled = creditPricing?.disabled === true;

    return (
        <Container style={isMobile ? mobileStyle : {}}>
            <UserTypeSelectionDialog />
            <LocalLoader open={loadingBlocking} blocking={true} />
            {loadingBalances && <Typography variant="h5" sx={{ fontWeight: 'bold' }} style={{ margin: '80px 0px 40px', color: colorText }}>Accounts</Typography>}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                {loadingBalances ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                        <CircularProgress />
                    </Box>
                ) : rowBalance
                    .filter(o => [RESOURCE.tokens].includes(o.resourceId))
                    .sort((a, b) => b.resourceId.localeCompare(a.resourceId))
                    .map((rowBalance) => {

                        return (
                            <Paper key={rowBalance.accountId + rowBalance.resourceId} sx={{ width: isMobile ? '100%' : '46.5%', height: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                                    <Typography variant={isMobile ? 'h8' : 'h5'} component="p" sx={{ color: colorSecondary, fontWeight: 'bold', flexGrow: 1, textAlign: 'center', mx: 2 }}>
                                        <AnimatedNumber number={rowBalance.balance} formatter={formatMoney} />&nbsp;
                                        <span style={{ color: formatMoneyTextColor(rowBalance.balance) }}>{getTokenNameByResourceId(rowBalance.resourceId)}</span>
                                        <Typography variant="body1" component="span" sx={{ display: 'block', color: colorText }}>
                                            {getFullTokenNameByResourceId(rowBalance.resourceId)}
                                        </Typography>
                                    </Typography>
                                    <Box sx={{ flexGrow: 1, textAlign: 'center', mx: 2 }}>

                                        <Typography variant={isMobile ? 'h8' : 'h5'} component="p" sx={{ fontWeight: 'bold' }}>
                                            {rowBalance.accountName}
                                        </Typography>
                                        {!isMobile && <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                            {rowBalance?.accountName && <Typography variant="caption" display="block"
                                                                                    style={{
                                                                                        fontSize: isMobile ? 8 : undefined,
                                                                                        marginTop: isMobile ? '-8px' : '6px'
                                                                                    }}>
                                                <strong style={{ visibility: isMobile ? 'hidden' : undefined }}>accountId:</strong> {rowBalance.accountId}
                                            </Typography>}
                                            {rowBalance?.accountName && <Tooltip title="Copy Account ID">
                                                <IconButton
                                                    onClick={() => navigator.clipboard.writeText(rowBalance.accountId)}
                                                    size="small"
                                                    style={{ marginTop: '4px' }}
                                                    sx={{ ml: 0 }}
                                                >
                                                    <ContentCopyIcon style={{ fontSize: isMobile ? 10 : 12 }} />
                                                </IconButton>
                                            </Tooltip>}
                                        </Box>}
                                    </Box>
                                </Box>
                            </Paper>
                        )
                    })}


                {!loadingBalances && (applicationAccounts?.length > 0 || showTransferTokens) ? <Paper
                    key={'transfer'}
                    sx={{
                        width: isMobile ? '100%' : '46.5%',
                        height: 100,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        p: 2
                    }}
                >
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        mb: 1
                    }}>
                        {getQueryParamValue('transfer') ? (
                            <TextField
                                value={customAccountId}
                                onChange={(e) => setCustomAccountId(e.target.value)}
                                size="small"
                                placeholder="Enter Account ID"
                                label="Account ID"
                                sx={{ flexGrow: 1 }}
                            />
                        ) : (
                            <Select
                                value={selectedAccount}
                                onChange={handleAccountChange}
                                size="small"
                                sx={{ flexGrow: 1 }}
                            >
                                {applicationAccounts.map((account) => {
                                    const title = decrypt(account.kbTitle, account.key);
                                    return (
                                        <MenuItem key={account.accountId} value={account.accountId}>
                                            {title?.length > 30
                                                ? `${title.substring(0, 15)}...${title.substring(title.length - 12)}`
                                                : title
                                            }
                                        </MenuItem>
                                    );
                                })}
                            </Select>
                        )}
                        <TextField
                            value={transferAmount}
                            onChange={handleTransferAmountChange}
                            size="small"
                            type="number"
                            InputProps={{
                                startAdornment: <InputAdornment position="start">Credits</InputAdornment>,
                            }}
                            sx={{ width: '50%' }}
                        />
                    </Box>
                    <Box sx={{
                        display: 'flex',
                        gap: 1,
                        justifyContent: 'flex-end'
                    }}>
                        <Button
                            variant="outlined"
                            color="primary"
                            onClick={handleFund}
                            style={{ width: '48%', color: colorGreen, borderColor: colorGreen }}
                        >
                            Send
                        </Button>
                        <Button
                            variant="outlined"
                            color="secondary"
                            onClick={handleGetBack}
                            style={{ width: '50%' }}
                        >
                            Get Back
                        </Button>
                    </Box>
                </Paper> : (applicationAccounts?.length > 0 && showTransferTokens? <CircularProgress /> : <div></div>) }

                {(getQueryParamValue('buy') || !loadingBalances && !isBusinessWithoutSubscription(userProfile) && (!parkedDomain || (creditPricing && !isCreditPricingDisabled)) && !(parkedDomain && selectedService === 'credits' && withoutSubscriptionOrDeveloper && isDeveloper === null)) && (
                    <Paper
                        key={'pay'}
                        sx={{ width: isMobile ? '100%' : '46.5%', height: 'auto', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', p: 2 }}
                    >
                        {loadingPricing ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4, width: '100%' }}>
                                <CircularProgress size={40} />
                            </Box>
                        ) : (
                        <>
                        <Box sx={{ width: (isMobile ? '100%' : '95%'), display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>
                            {showServiceMenu && (
                                <FormControl size="small" fullWidth>
                                    <InputLabel>Service Type</InputLabel>
                                    <Select
                                        value={selectedService}
                                        label="Service Type"
                                        onChange={(e) => setSelectedService(e.target.value)}
                                    >
                                        {Object.values(CONSULTING_SERVICES).map((service) => (
                                            <MenuItem key={service.id} value={service.id}>
                                                {service.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            )}
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <TextField
                                        label={isAmountValid ?
                                            (showServiceMenu ? `Amount to Pay` : `Buy ${getFullTokenNameByResourceId(RESOURCE.tokens)}`) :
                                            `Minimum amount is ${creditPricing?.symbol || '€'}10`}
                                        value={depositAmountEUR}
                                        onChange={handleDepositChange}
                                        InputProps={{
                                            startAdornment: <InputAdornment position="start">{creditPricing?.symbol || '€'}</InputAdornment>,
                                        }}
                                        variant="outlined"
                                        size="small"
                                        fullWidth
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                '& fieldset': {
                                                    borderColor: isAmountValid ? 'inherit' : colorEasyRed,
                                                },
                                            },
                                        }}
                                    />
                                    <Button onClick={handleBuyClick}
                                            disabled={!isAmountValid}
                                            variant="contained"
                                            color={'success'}
                                            sx={{ height: '40px', width: '100px' }}>
                                        {showServiceMenu ? 'Pay' : 'Buy'}
                                    </Button>
                                </Box>
                                {selectedService === 'credits' && depositAmountEUR && !isNaN(depositAmountEUR) && isAmountValid && (
                                    <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                        You will receive {Math.floor(depositAmountEUR * (creditPricing?.creditsPer10Units || 1000) / 10).toLocaleString()} credits
                                    </Typography>
                                )}
                            </Box>
                        </Box>
                        {(selectedService === 'credits' || !showServiceMenu) && (
                            <Box sx={{ width: (isMobile ? '100%' : '95%') }}>
                                {resources.map((resource, index) => (
                                    <Box key={index} sx={{ position: 'relative', mb: 1.5 }}>
                                        <Chip label={resource.model} color="primary" size="small" sx={{ position: 'absolute', left: 0, top: '50%', transform: 'translateY(-50%)', zIndex: 1, width: '100px' }} />
                                        <Box sx={{ pl: '110px' }}>
                                            <Typography variant="subtitle1" sx={{ mt: 1, fontSize: 16 }}>{resource.label}</Typography>
                                            <Tooltip title={`${resource.quantity.toLocaleString()} ${resource.items}`}>
                                                <LinearProgress
                                                    variant="determinate" value={(resource.quantity / maxTokens) * resource.itemPrice} />
                                            </Tooltip>
                                            <Typography variant="body2" sx={{ mt: 0.5, fontSize: 16 }}>~ {resource.quantity.toLocaleString()} {resource.suffix || resource.items}</Typography>
                                        </Box>
                                    </Box>
                                ))}
                            </Box>
                        )}
                        
                        {showServiceMenu && selectedService !== 'credits' && (
                            <Box sx={{ width: (isMobile ? '100%' : '95%'), textAlign: 'center', py: 2 }}>
                                <Typography variant="body1" color="text.secondary">
                                    {getServiceById(selectedService).description}
                                </Typography>
                            </Box>
                        )}
                        </>
                        )}
                    </Paper>
                )}
            </Box>
            {settingsLoaded && !hideTransactions && (
            <>
            <Typography variant="h5" sx={{ fontWeight: 'bold' }} style={{ margin: '80px 0px 40px', color: colorText }}>Transactions</Typography>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h5" sx={{ fontWeight: 'bold', color: colorText }}>Accounts</Typography>
                <Select
                    value={selectedTransactionsAccount}
                    onChange={handleTransactionsAccountChange}
                    size="small"
                >
                    <MenuItem value={userProfile?.accountId}>User Account</MenuItem>
                    {applicationAccounts.map((account) => (
                        <MenuItem key={account.accountId} value={account.accountId}>
                            {decrypt(account.kbTitle, account.key)}
                        </MenuItem>
                    ))}
                </Select>
            </Box>

            {loadingTransactions && !transactions?.length ? (
                <CircularProgress />
            ) : isMobile ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {transactions.filter(tx => !parkedDomain || tx.amount < 0).map((tx, index) => (
                        <Card key={tx.id} className={isNew(tx) ? 'new-item-animation' : ''}>
                            <CardContent>
                                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', paddingTop: 1 }}>
                                        <TransactionActionsMenu balance={balance} transaction={tx} onRefund={handleRefund} refunded={refunded} />
                                    </Box>
                                    <Box sx={{ flexGrow: 1 }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Typography variant="body2" sx={{ textAlign: 'left' }}>Date:</Typography>
                                            <Typography variant="body2" sx={{ textAlign: 'right' }}>{tx.date}</Typography>
                                        </Box>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Typography variant="body2" sx={{ textAlign: 'left' }}>Reason:</Typography>
                                            <Typography variant="body2" sx={{ textAlign: 'right' }}>{tx.reason?.split('\n\n')[0]}</Typography>
                                        </Box>
                                        {!parkedDomain && <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Typography variant="body2" sx={{ textAlign: 'left' }}>My Account:</Typography>
                                            <Typography variant="body2" sx={{ textAlign: 'right' }}>{namedAccounts[tx.accountId] || tx.accountId.substring(0, 8) + '...'}</Typography>
                                        </Box>}
                                        {!parkedDomain && <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Typography variant="body2" sx={{ textAlign: 'left' }}>Counterparty:</Typography>
                                            <Typography variant="body2" sx={{ textAlign: 'right' }}>{serviceAccounts?.[tx.remoteAccount] || tx.remoteAccount}</Typography>
                                        </Box>}
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Typography variant="body2" sx={{ textAlign: 'left' }}>Amount:</Typography>
                                            <Typography variant="body2" sx={{ textAlign: 'right', color: colorSecondary }}>{formatMoney(tx.amount)}</Typography>
                                        </Box>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Typography variant="body2" sx={{ textAlign: 'left' }}>Resource:</Typography>
                                            <Typography variant="body2" sx={{ textAlign: 'right' }}>{tx.resourceId}</Typography>
                                        </Box>
                                    </Box>
                                </Box>
                            </CardContent>
                        </Card>
                    ))}
                </Box>
            ) : (
                <Paper>
                    <Box sx={{ overflow: 'auto' }}>
                        <Table sx={{ minWidth: 650 }} aria-label="simple table">
                            <TableHead>
                                <TableRow>
                                    <TableCell style={{ width: '20%' }}>Date</TableCell>
                                    <TableCell align="left" style={{ width: '30%' }}>Reason</TableCell>
                                    {!parkedDomain && <TableCell align="left" style={{ width: '20%' }}>My Account</TableCell>}
                                    {!parkedDomain && <TableCell align="left" style={{ width: '20%' }}>Counterparty</TableCell>}
                                    <TableCell align="left" style={{ width: '10%' }}>Amount</TableCell>
                                    <TableCell align="left" style={{ width: '5%' }}>Resource</TableCell>
                                    {!parkedDomain && <TableCell align="left" style={{ width: '10%' }}>Action</TableCell>}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {transactions.filter(tx => !parkedDomain || tx.amount < 0).map((tx) => (
                                    <TableRow
                                        className={isNew(tx) ? 'new-item-animation' : ''}
                                        key={`${tx.id}`}
                                        sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                                    >
                                        <TableCell component="th" scope="row">
                                            {tx.date}
                                        </TableCell>
                                        <TableCell align="left">{tx.reason?.split('\n\n')[0]}</TableCell>
                                        {!parkedDomain && <TableCell align="left">{namedAccounts[tx.accountId] || tx.accountId}</TableCell>}
                                        {!parkedDomain && <TableCell align="left">{serviceAccounts?.[tx.remoteAccount] || tx.remoteAccount}</TableCell>}
                                        <TableCell align="left" style={{ color: colorSecondary }}>{formatMoney(tx.amount)}</TableCell>
                                        <TableCell align="left">{tx.resourceId}</TableCell>
                                        {!parkedDomain && <TableCell align="left">
                                            {tx.id && (
                                                <TransactionActionsMenu balance={balance} transaction={tx} onRefund={handleRefund} refunded={refunded} />
                                            )}
                                        </TableCell>}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </Box>
                </Paper>
            )}

            {!loadingTransactions && hasMoreTransactions && transactions?.length >= transactionsLimit && (
                <Box style={{ marginTop: '20px' }}>
                    <Button onClick={loadMoreTransactions} variant="contained" size="small" color="primary">
                        Load More
                    </Button>
                </Box>
            )}
            </>
            )}

        </Container>
    );
};